import { useDispatch } from 'react-redux';
import Modal from '../../../../Common/Modal';
import styles from './EditPhone.module.scss';
import { Button } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import clx from 'classnames';
import { Phone, BadgeCheck } from 'lucide-react';
import IOSSwitch from 'Containers/Common/CustomSwitch';
import { clientActions } from '../../../store';
import { useTranslation } from 'react-i18next';

const sanitizePhone = (raw) =>
  String(raw ?? '')
    .replace(/[^\d+\s()-]/g, '')
    .slice(0, 20);

const PHONE_REGEX =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const EditPhone = ({ phone, verified, client_id, isModalOpen, setIsModalOpen }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editedPhone, setEditedPhone] = useState(phone || '');
  const [isVerified, setIsVerified] = useState(Boolean(verified));
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  const isAdding = !String(phone || '').trim();

  useEffect(() => {
    if (!isModalOpen) return;
    setEditedPhone(phone || '');
    setIsVerified(Boolean(verified));
    setSubmitting(false);
    setValidationError('');
  }, [isModalOpen, phone, verified]);

  const hasChanges = useMemo(() => {
    const nextPhone = String(editedPhone || '').trim();
    const currentPhone = String(phone || '').trim();
    return nextPhone !== currentPhone || Boolean(isVerified) !== Boolean(verified);
  }, [editedPhone, isVerified, phone, verified]);

  const closeModal = () => {
    setEditedPhone(phone || '');
    setIsVerified(Boolean(verified));
    setSubmitting(false);
    setValidationError('');
    setIsModalOpen(false);
  };

  const handleSave = () => {
    const nextPhone = String(editedPhone || '').trim();
    if (!hasChanges || submitting || !nextPhone) return;

    if (!PHONE_REGEX.test(nextPhone)) {
      setValidationError(t('clients.clientModal.editPhone.invalidPhone'));
      return;
    }

    setValidationError('');
    setSubmitting(true);
    dispatch(
      clientActions.EDIT_CLIENT_PHONE.REQUEST(
        {
          user: {
            idutente: client_id,
          },
          phone: nextPhone,
          verified: isVerified,
        },
        () => {
          dispatch(
            clientActions.GET_CLIENT.REQUEST({ idcliente: client_id }, () => {
              setSubmitting(false);
              setIsModalOpen(false);
            }),
          );
        },
      ),
    );
    window.setTimeout(() => setSubmitting(false), 2500);
  };

  return (
    <Modal
      isModalOpen={isModalOpen}
      setIsModalOpen={closeModal}
      title={t(
        isAdding ? 'clients.clientModal.editPhone.addTitle' : 'clients.clientModal.editPhone.title',
      )}
      maxWidth="min(400px, calc(100vw - 32px))"
      width="100%"
      height="auto"
      maxHeight="90dvh"
      showCloseBtn
      centered
      borderRadius="16px"
    >
      <div className={styles.shell}>
        <div className={styles.body}>
          <div className={styles.intro}>
            <span className={styles.intro_icon} aria-hidden="true">
              <Phone size={20} strokeWidth={2.2} />
            </span>
            <div className={styles.intro_copy}>
              <p className={styles.intro_title}>
                {t(
                  isAdding
                    ? 'clients.clientModal.editPhone.addIntroTitle'
                    : 'clients.clientModal.editPhone.introTitle',
                )}
              </p>
              <p className={styles.intro_text}>
                {t(
                  isAdding
                    ? 'clients.clientModal.editPhone.addIntroText'
                    : 'clients.clientModal.editPhone.introText',
                )}
              </p>
            </div>
          </div>

          <section className={styles.section}>
            <label className={styles.label} htmlFor="edit-client-phone">
              {t('clients.clientModal.editPhone.numberLabel')}
            </label>
            <div className={styles.input_wrap}>
              <Phone size={16} strokeWidth={2.2} className={styles.input_icon} aria-hidden="true" />
              <input
                id="edit-client-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                className={styles.input}
                value={editedPhone}
                onChange={(event) => {
                  setEditedPhone(sanitizePhone(event.target.value));
                  setValidationError('');
                }}
                placeholder={t('clients.clientModal.editPhone.numberPlaceholder')}
                disabled={submitting}
              />
            </div>
            {validationError ? (
              <p className={styles.intro_text} style={{ color: '#d14343', margin: 0 }}>
                {validationError}
              </p>
            ) : null}
          </section>

          <section
            className={clx(styles.verified_card, {
              [styles.verified_card_on]: isVerified,
            })}
          >
            <div className={styles.verified_copy}>
              <span className={styles.verified_label}>
                {t('clients.clientModal.editPhone.verifiedNumberLabel')}
              </span>
              <span className={styles.verified_hint}>
                {isVerified
                  ? t('clients.clientModal.editPhone.verifiedHintOn')
                  : t('clients.clientModal.editPhone.verifiedHintOff')}
              </span>
            </div>
            <div className={styles.verified_control}>
              {isVerified ? (
                <span className={styles.verified_badge}>
                  <BadgeCheck size={14} strokeWidth={2.4} aria-hidden="true" />
                  {t('common.actions.verified')}
                </span>
              ) : null}
              <IOSSwitch
                checked={isVerified}
                onChange={(_event, checked) => setIsVerified(Boolean(checked))}
                disabled={submitting}
                inputProps={{
                  'aria-label': t('clients.clientModal.editPhone.verifiedNumberLabel'),
                }}
              />
            </div>
          </section>
        </div>

        <div className={styles.footer}>
          <Button
            variant="contained"
            className={styles.confirm_button}
            onClick={handleSave}
            disabled={!hasChanges || submitting || !String(editedPhone || '').trim()}
          >
            {t('common.actions.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditPhone;
